import { ActionTree, GetterTree, Module, MutationTree } from "vuex"
import { RootState } from "@/store/index"
import { Location } from "@/types/setup"
import axios from "axios"

const STORE_NAME = "location"

export const EVSE_FEATURE_CODE = "AI_STRATEGY_EVSE"

export enum LoadingTypes {
    LOCATION = "LOCATION",
}

export type LocationFeature = {
    code: string
    name?: string
    [key: string]: any
}

export type LocationState = {
    locationId: string | number | null
    pendingLocationId: string | number | null
    location: Location | null
    features: LocationFeature[]
    loading: Record<LoadingTypes, boolean>
}

type LocationApiResponse = {
    data: {
        location: Location
        features?: LocationFeature[]
    }
}

export enum LocationMutations {
    SET_LOADING = "setLoading",
    SET_PENDING_LOCATION_ID = "setPendingLocationId",
    SET_LOCATION = "setLocation",
}

export const LocationActions = {
    FETCH_LOCATION: `${STORE_NAME}/fetchLocation`,
} as const

const state: LocationState = {
    locationId: null,
    pendingLocationId: null,
    location: null,
    features: [],
    loading: {
        [LoadingTypes.LOCATION]: false,
    },
}

const mutations: MutationTree<LocationState> = {
    setLoading(state, payload: { type: LoadingTypes; loading: boolean }) {
        state.loading = {
            ...state.loading,
            [payload.type]: payload.loading,
        }
    },
    setPendingLocationId(state, locationId: string | number | null) {
        state.pendingLocationId = locationId
    },
    setLocation(
        state,
        payload: {
            locationId: string | number
            location: Location | null
            features: LocationFeature[]
        },
    ) {
        state.locationId = payload.locationId
        state.location = payload.location
        state.features = payload.features
    },
}

const actions: ActionTree<LocationState, RootState> = {
    async fetchLocation(
        { commit, state },
        payload: { locationId: string | number; force?: boolean },
    ) {
        const { locationId, force = false } = payload

        if (!locationId) return
        if (String(state.pendingLocationId) === String(locationId)) return
        if (!force && String(state.locationId) === String(locationId)) return

        commit(LocationMutations.SET_PENDING_LOCATION_ID, locationId)
        commit(LocationMutations.SET_LOADING, {
            type: LoadingTypes.LOCATION,
            loading: true,
        })

        try {
            const res = await axios.get<LocationApiResponse>(`/locations/${locationId}`)
            commit(LocationMutations.SET_LOCATION, {
                locationId,
                location: res.data.data.location,
                features: res.data.data.features ?? [],
            })
        } catch (e) {
            commit(LocationMutations.SET_LOCATION, { locationId, location: null, features: [] })
        } finally {
            commit(LocationMutations.SET_PENDING_LOCATION_ID, null)
            commit(LocationMutations.SET_LOADING, {
                type: LoadingTypes.LOCATION,
                loading: false,
            })
        }
    },
}

const getters: GetterTree<LocationState, RootState> = {
    location: ({ location }) => location,
    owner: ({ location }) => location?.owner ?? null,
    features: ({ features }) => features,
    loading: ({ loading }) => loading,
    hasEvseFeature: ({ features }) => features.some((f) => f.code === EVSE_FEATURE_CODE),
}

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
} as Module<LocationState, RootState>
